import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, User } from "lucide-react";
import { useMyQuotes, isQuoteExpired } from "@/hooks/useQuotes";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  sent:    { label: "En attente", variant: "secondary" },
  viewed:  { label: "Vu",         variant: "secondary" },
  accepted: { label: "Accepté",   variant: "default" },
};

const DashboardDevisPreview = () => {
  const lp = useLocalizedPath();
  const { sentQuotes, loading } = useMyQuotes();

  // Devis actifs (sent/viewed non expirés), max 3, les plus récents en premier
  const active = sentQuotes
    .filter((q) => (q.status === "sent" || q.status === "viewed") && !isQuoteExpired(q))
    .slice(0, 3);

  if (loading) {
    return (
      <section className="space-y-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Mes devis
        </h2>
        <div className="py-6 text-center text-sm text-muted-foreground">Chargement…</div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Mes devis
        </h2>
        <a href={lp("/mes-devis")}>
          <Button variant="ghost" size="sm" className="text-xs gap-1">
            Voir tout <DirectionalArrow className="h-3 w-3" />
          </Button>
        </a>
      </div>

      {active.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground/30" />
            <p className="mt-2 text-sm text-muted-foreground">Aucun devis en attente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {active.map((quote) => {
            const config = STATUS_CONFIG[quote.status] || STATUS_CONFIG.sent;
            return (
              <Card key={quote.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={config.variant} className="text-[10px]">{config.label}</Badge>
                        <span className="text-xs text-muted-foreground">
                          Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                        </span>
                      </div>
                      <p className="text-sm font-medium truncate">{quote.title}</p>
                      <p className="text-xs font-bold text-primary">
                        {quote.total_amount.toLocaleString("fr-DZ")} DZD
                      </p>
                    </div>
                    {quote.conversation_id ? (
                      <a href={lp(`/messagerie?conversation=${quote.conversation_id}`)}>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 shrink-0">
                          <User className="h-3 w-3" />
                        </Button>
                      </a>
                    ) : (
                      <a href={lp(`/devis`)}>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 shrink-0">
                          <FileText className="h-3 w-3" />
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DashboardDevisPreview;
